// Localized mirrors of the shared journey enum label maps. The shared
// JOURNEY_*_LABELS / CONDITION_OP_LABELS from @ckgo2/shared are EN-only and are
// not registered in @/i18n/enumLabels, so the journey pages build their own
// Record<Value, string> from typed dictionary keys. Module scope is safe —
// switching the admin language reloads the page.
import i18n from "@/i18n";
import type {
  JourneySubjectType,
  JourneyTriggerType,
  JourneySendClass,
  JourneyReenrollment,
  JourneyActionType,
  ConditionOp,
  EnrollmentStatus,
  StepRunStatus,
} from "@ckgo2/shared";

export const SUBJECT_TYPE_LABELS: Record<JourneySubjectType, string> = {
  order: i18n.t("marketing.journeyEnums.subjectType.order"),
  lead: i18n.t("marketing.journeyEnums.subjectType.lead"),
  customer: i18n.t("marketing.journeyEnums.subjectType.customer"),
  service_booking: i18n.t("marketing.journeyEnums.subjectType.service_booking"),
  trip_date: i18n.t("marketing.journeyEnums.subjectType.trip_date"),
  invoice: i18n.t("marketing.journeyEnums.subjectType.invoice"),
  review: i18n.t("marketing.journeyEnums.subjectType.review"),
  complaint: i18n.t("marketing.journeyEnums.subjectType.complaint"),
  participant: i18n.t("marketing.journeyEnums.subjectType.participant"),
};

export const TRIGGER_TYPE_LABELS: Record<JourneyTriggerType, string> = {
  status_change: i18n.t("marketing.journeyEnums.triggerType.status_change"),
  field_change: i18n.t("marketing.journeyEnums.triggerType.field_change"),
  time_offset: i18n.t("marketing.journeyEnums.triggerType.time_offset"),
  event: i18n.t("marketing.journeyEnums.triggerType.event"),
  inactivity: i18n.t("marketing.journeyEnums.triggerType.inactivity"),
  recurring: i18n.t("marketing.journeyEnums.triggerType.recurring"),
  manual: i18n.t("marketing.journeyEnums.triggerType.manual"),
};

export const SEND_CLASS_LABELS: Record<JourneySendClass, string> = {
  marketing: i18n.t("marketing.journeyEnums.sendClass.marketing"),
  transactional: i18n.t("marketing.journeyEnums.sendClass.transactional"),
  ops: i18n.t("marketing.journeyEnums.sendClass.ops"),
};

export const REENROLLMENT_LABELS: Record<JourneyReenrollment, string> = {
  once: i18n.t("marketing.journeyEnums.reenrollment.once"),
  once_per_completion: i18n.t("marketing.journeyEnums.reenrollment.once_per_completion"),
  always: i18n.t("marketing.journeyEnums.reenrollment.always"),
};

export const ACTION_TYPE_LABELS: Record<JourneyActionType, string> = {
  send_email: i18n.t("marketing.journeyEnums.actionType.send_email"),
  send_sms: i18n.t("marketing.journeyEnums.actionType.send_sms"),
  send_whatsapp: i18n.t("marketing.journeyEnums.actionType.send_whatsapp"),
  create_task: i18n.t("marketing.journeyEnums.actionType.create_task"),
  assign: i18n.t("marketing.journeyEnums.actionType.assign"),
  notify_user: i18n.t("marketing.journeyEnums.actionType.notify_user"),
  add_to_segment: i18n.t("marketing.journeyEnums.actionType.add_to_segment"),
  remove_from_segment: i18n.t("marketing.journeyEnums.actionType.remove_from_segment"),
  generate_document: i18n.t("marketing.journeyEnums.actionType.generate_document"),
  webhook: i18n.t("marketing.journeyEnums.actionType.webhook"),
  agent_action: i18n.t("marketing.journeyEnums.actionType.agent_action"),
};

export const CONDITION_OP_LABELS: Record<ConditionOp, string> = {
  eq: i18n.t("marketing.journeyEnums.conditionOp.eq"),
  neq: i18n.t("marketing.journeyEnums.conditionOp.neq"),
  gt: i18n.t("marketing.journeyEnums.conditionOp.gt"),
  lt: i18n.t("marketing.journeyEnums.conditionOp.lt"),
  gte: i18n.t("marketing.journeyEnums.conditionOp.gte"),
  lte: i18n.t("marketing.journeyEnums.conditionOp.lte"),
  contains: i18n.t("marketing.journeyEnums.conditionOp.contains"),
  in: i18n.t("marketing.journeyEnums.conditionOp.in"),
  is_set: i18n.t("marketing.journeyEnums.conditionOp.is_set"),
  is_empty: i18n.t("marketing.journeyEnums.conditionOp.is_empty"),
};

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  active: i18n.t("marketing.journeyEnums.enrollmentStatus.active"),
  completed: i18n.t("marketing.journeyEnums.enrollmentStatus.completed"),
  cancelled: i18n.t("marketing.journeyEnums.enrollmentStatus.cancelled"),
  failed: i18n.t("marketing.journeyEnums.enrollmentStatus.failed"),
};

export const STEP_RUN_STATUS_LABELS: Record<StepRunStatus, string> = {
  completed: i18n.t("marketing.journeyEnums.stepRunStatus.completed"),
  skipped: i18n.t("marketing.journeyEnums.stepRunStatus.skipped"),
  failed: i18n.t("marketing.journeyEnums.stepRunStatus.failed"),
};

// Per-action run statuses inside a step run are untyped strings from the API —
// map the known ones, fall back to the raw value at the call site.
export const ACTION_RUN_STATUS_LABELS: Record<string, string> = {
  ok: i18n.t("marketing.journeyEnums.actionRunStatus.ok"),
  sent: i18n.t("marketing.journeyEnums.actionRunStatus.sent"),
  completed: i18n.t("marketing.journeyEnums.actionRunStatus.completed"),
  skipped: i18n.t("marketing.journeyEnums.actionRunStatus.skipped"),
  failed: i18n.t("marketing.journeyEnums.actionRunStatus.failed"),
};
